import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { toast } from "sonner";

export default function Messages() {
  useSeo({ title: "Messages — BookFreely", path: "/messages", noindex: true });
  const { user, authHeader, loading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [away, setAway] = useState(!!user?.away_mode);
  const [awayMsg, setAwayMsg] = useState(user?.away_message || "");
  const [savingAway, setSavingAway] = useState(false);
  const [params] = useSearchParams();
  const scrollRef = useRef(null);
  const nav = useNavigate();

  const loadThreads = async () => {
    const r = await axios.get(`${API}/messages/threads`, { headers: authHeader() });
    setThreads(r.data.threads);
    return r.data.threads;
  };

  const openThread = async (id) => {
    setActiveId(id);
    const r = await axios.get(`${API}/messages/thread/${id}`, { headers: authHeader() });
    setThread(r.data.thread);
    setMessages(r.data.messages);
    // refresh thread unread counters
    setTimeout(loadThreads, 200);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/messages"); return; }
    (async () => {
      const ts = await loadThreads();
      const requested = params.get("thread");
      const pick = requested || (ts[0] && ts[0].id);
      if (pick) openThread(pick);
    })();
    // eslint-disable-next-line
  }, [loading, user?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    setAway(!!user?.away_mode);
    setAwayMsg(user?.away_message || "");
  }, [user?.id]);

  const saveAway = async (nextAway, nextMsg) => {
    setSavingAway(true);
    try {
      await axios.patch(`${API}/business/profile`,
        { away_mode: nextAway, away_message: (nextMsg || "").trim() || " " },
        { headers: authHeader() });
      toast.success(nextAway ? "Away Mode on — new messages get an instant auto-reply" : "Away Mode turned off");
    } catch (err) {
      toast.error("Could not update Away Mode");
      setAway(!nextAway);
    } finally { setSavingAway(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !activeId || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/messages/thread/${activeId}`, { text: t }, { headers: authHeader() });
      setMessages((m) => [...m, r.data.message]);
      setText("");
      loadThreads();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  if (loading || !user) return <div className="App"><Header hideSearch /><div className="max-w-5xl mx-auto p-12" style={{ color: "var(--gf-text-muted)" }}>Loading...</div></div>;

  const youAreCustomer = user.role === "customer";

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Messages</div>
        <h1 className="font-display text-3xl mt-2" style={{ color: "var(--gf-text)" }}>Conversations</h1>

        {user.role === "business" && (
          <div className="mt-4 rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="away-mode-card">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium" style={{ color: "var(--gf-text)" }}>Away Mode</span>
                {away && <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }} data-testid="away-mode-status">On</span>}
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>When on, new customer messages get an instant auto-reply so no one is left waiting. Bookings are unaffected.</p>
              {away && (
                <input data-testid="away-message-input" value={awayMsg}
                  onChange={(e) => setAwayMsg(e.target.value)}
                  onBlur={() => saveAway(true, awayMsg)}
                  placeholder="Custom auto-reply message (optional)"
                  className="mt-3 w-full bg-transparent border rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--gf-accent)]"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
              )}
            </div>
            <button data-testid="away-mode-toggle" role="switch" aria-checked={away} disabled={savingAway}
              onClick={() => { const n = !away; setAway(n); saveAway(n, awayMsg); }}
              className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60"
              style={{ background: away ? "var(--gf-accent)" : "var(--gf-border-strong)" }}>
              <span className="inline-block h-5 w-5 rounded-full bg-white transition-transform" style={{ transform: away ? "translateX(22px)" : "translateX(4px)" }} />
            </button>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-3 gap-4 rounded-2xl border" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          {/* threads list */}
          <aside className="border-r p-3 max-h-[70vh] overflow-y-auto" style={{ borderColor: "var(--gf-border)" }} data-testid="threads-list">
            {threads.length === 0 ? (
              <div className="text-sm p-4" style={{ color: "var(--gf-text-muted)" }}>No conversations yet. {youAreCustomer ? "Message a business from their profile page." : "Customers will start conversations with you here."}</div>
            ) : threads.map((t) => {
              const title = youAreCustomer ? t.business_name : t.customer_name;
              return (
                <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => openThread(t.id)}
                  className="w-full text-left p-3 rounded-lg transition-colors"
                  style={{ background: activeId === t.id ? "var(--gf-surface-2)" : "transparent", color: "var(--gf-text)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{title}</span>
                    {t.unread > 0 && (
                      <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>{t.unread}</span>
                    )}
                  </div>
                  <div className="text-xs truncate mt-1" style={{ color: "var(--gf-text-muted)" }}>
                    {t.last_from_role === user.role ? "You: " : ""}{t.last_text}
                  </div>
                </button>
              );
            })}
          </aside>

          {/* active thread */}
          <section className="md:col-span-2 flex flex-col max-h-[70vh]" data-testid="thread-pane">
            {!thread ? (
              <div className="p-8 text-sm" style={{ color: "var(--gf-text-muted)" }}>Select a conversation to view.</div>
            ) : (
              <>
                <div className="p-4 border-b" style={{ borderColor: "var(--gf-border)" }}>
                  <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{youAreCustomer ? "Business" : "Customer"}</div>
                  <div className="font-display text-xl" style={{ color: "var(--gf-text)" }}>
                    {youAreCustomer ? thread.business_name : thread.customer_name}
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages">
                  {messages.map((m) => {
                    const mine = m.from_id === user.id;
                    return (
                      <div key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")}>
                        <div className="max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line"
                          style={mine ? { background: "var(--gf-accent)", color: "var(--gf-on-accent)" } : { background: "var(--gf-surface-2)", color: "var(--gf-text)", border: "1px solid var(--gf-border)" }}>
                          {m.text}
                          <div className="text-[10px] mt-1 opacity-70">{m.auto_reply ? "Auto-reply · " : ""}{new Date(m.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="p-3 border-t flex gap-2" style={{ borderColor: "var(--gf-border)" }}>
                  <input data-testid="reply-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply..."
                    className="flex-1 bg-transparent border rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--gf-accent)]"
                    style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} />
                  <button data-testid="reply-send-btn" disabled={sending || !text.trim()} className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
                    style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>Send</button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
